import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';

import { Contact } from '../classes/models';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class ContactService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  getContacts(query: { [key: string]: any } = {}): Observable<Contact[]> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<Contact[]>;
        } = {
          Vendor: () =>
            this.http.get<Contact[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/contacts/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<Contact[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/contacts/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<Contact[]>(`/api/v2/admin/contacts/many`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  getContactCount(query: { [key: string]: any } = {}): Observable<number> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/contacts/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/contacts/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/contacts/count`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  saveContact(Contact: Contact) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.post(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/contacts`,
              Contact
            ),
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/contacts`,
              Contact
            ),
          Admin: () => this.http.post(`/api/v2/admin/contacts`, Contact)
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  deleteContact(id: number) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.delete(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/contacts/${id}`
            ),
          Admin: () => this.http.delete(`/api/v2/admin/contacts/${id}`),
          Vendor: () =>
            this.http.delete(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/contacts/${id}`
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  /* We will only allow archiving for the time being.
  deleteContact(id: number) {
    return this.AuthService.getUserType()
      .pipe(
        take(1),
        concatMap((userType: string) => {
          const userTypeToObservableMap: {
            [key: string]: () => Observable<any>
          } = {
            'Vendor': () => this.http.delete(`/api/v2/vendor/${this.AuthService.getUserVendorId()}/contacts/${id}`),
            'Client': () => this.http.delete(`/api/v2/client/${this.AuthService.getUserCompanyId()}/contacts/${id}`),
            'Admin': () => this.http.delete(`/api/v2/admin/contacts/${id}`)
          }

          if (userTypeToObservableMap[userType]) {
            return userTypeToObservableMap[userType]();
          }

          this.ToastService.addErrorToast('Error', 'Invalid permission.');
          throw new Error('Invalid permission.');
        })
      );
  }
  */
}
